import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, X, Paperclip, Filter } from "lucide-react";

const TYPES = { vacation: "Urlaub", sick: "Krank", special: "Sonderurlaub", training: "Fortbildung", maternity: "Mutterschutz" };
const STATUS = { pending: "Ausstehend", approved: "Genehmigt", rejected: "Abgelehnt" };
const STATUS_COLORS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-rose-100 text-rose-800" };

export default function AdminRequestsPage() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    const [r, u] = await Promise.all([api.get(`/absences${q}`), api.get("/users")]);
    setItems(r.data);
    setUsers(u.data);
  };
  useEffect(() => { load(); }, [filter]);

  const decide = async (id, approved) => {
    try {
      await api.post(`/absences/${id}/decide`, { approved });
      toast.success(approved ? "Genehmigt" : "Abgelehnt");
      await load();
    } catch (err) { toast.error("Fehler"); }
  };

  const userName = (id) => {
    const u = users.find(x => x.id === id);
    return u ? `${u.first_name} ${u.last_name}` : id;
  };

  const pending = items.filter(i => i.status === "pending").length;

  return (
    <div className="space-y-6" data-testid="requests-root">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <div className="overline">Verwaltung</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Anträge & Abwesenheiten</h2>
          <p className="text-sm text-slate-500 mt-1">Zentrale Verwaltung aller Urlaubs- und Krankheitsanträge.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Filter className="w-4 h-4 text-slate-500" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="req-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Ausstehend {pending > 0 && `(${pending})`}</SelectItem>
              <SelectItem value="approved">Genehmigt</SelectItem>
              <SelectItem value="rejected">Abgelehnt</SelectItem>
              <SelectItem value="all">Alle</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ["Ausstehend", items.filter(i => i.status === "pending").length, "amber"],
          ["Genehmigt (Anz.)", items.filter(i => i.status === "approved").length, "emerald"],
          ["Abgelehnt", items.filter(i => i.status === "rejected").length, "rose"],
          ["Gesamt", items.length, "blue"],
        ].map(([lbl, val]) => (
          <Card key={lbl} className="border-border/60"><CardContent className="p-4">
            <div className="overline mb-1">{lbl}</div>
            <div className="font-heading text-2xl font-extrabold">{val}</div>
          </CardContent></Card>
        ))}
      </div>

      <Card className="border-border/60">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mitarbeiter</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Zeitraum</TableHead>
                <TableHead>Umfang</TableHead>
                <TableHead>Grund / Attest</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} data-testid={`req-row-${a.id}`}>
                  <TableCell className="font-medium">{userName(a.user_id)}</TableCell>
                  <TableCell><Badge variant="outline">{TYPES[a.type] || a.type}</Badge></TableCell>
                  <TableCell className="text-sm">{a.start_date} → {a.end_date}</TableCell>
                  <TableCell className="font-semibold">{a.mode === "hours" ? `${a.hours}h` : `${a.days} Tage`}</TableCell>
                  <TableCell className="text-sm max-w-xs">
                    <div className="truncate">{a.reason || "—"}</div>
                    {a.attachment_b64 && (
                      <a href={a.attachment_b64} download={a.attachment_name} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
                        <Paperclip className="w-3 h-3" /> {a.attachment_name}
                      </a>
                    )}
                  </TableCell>
                  <TableCell><Badge className={`${STATUS_COLORS[a.status]} hover:${STATUS_COLORS[a.status]}`}>{STATUS[a.status]}</Badge></TableCell>
                  <TableCell className="text-right">
                    {a.status === "pending" && (
                      <div className="flex justify-end gap-1">
                        <Button data-testid={`req-approve-${a.id}`} size="sm" variant="outline" className="text-emerald-700" onClick={() => decide(a.id, true)}>
                          <Check className="w-4 h-4 mr-1" /> Genehmigen
                        </Button>
                        <Button data-testid={`req-reject-${a.id}`} size="sm" variant="outline" className="text-rose-700" onClick={() => decide(a.id, false)}>
                          <X className="w-4 h-4 mr-1" /> Ablehnen
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Keine Anträge</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
